import React, { useState } from "react";
import {
  MenuItem,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  Menu,
  Fade,
} from "@mui/material";
import Grid from "@mui/material/Grid";

interface DropdownProps {
  options: (string | { id: string | number; name: string })[];
  value: (string | number) | (string | number)[];
  onChange: (value: (string | number) | (string | number)[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  width?: string | number;
  disabled?: boolean;
  checkmark?: boolean;
  showSelectedOptions?: boolean;
  variant?: "outlined" | "filled" | "standard";
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  variant = "outlined",
  label = "Select an Option",
  required = false,
  error = false,
  helperText = "",
  width = "100%",
  disabled = false,
  checkmark = false,
  showSelectedOptions = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  const normalizedOptions = options.map((option) =>
    typeof option === "string" ? { id: option, name: option } : option
  );

  const selectedOptions: (string | number)[] = checkmark
    ? (Array.isArray(value) ? (value as (string | number)[]) : [])
    : value
      ? [value as string | number]
      : [];


  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleOptionChange = (optionId: string | number) => {
    if (checkmark) {
      if (optionId === "select_all") {
        const allSelected = selectedOptions.length === normalizedOptions.length;
        onChange(allSelected ? [] : normalizedOptions.map((option) => option.id));
      } else {
        const updatedSelections = selectedOptions.includes(optionId)
          ? selectedOptions.filter((id) => id !== optionId)
          : [...selectedOptions, optionId];
        onChange(updatedSelections);
      }
    } else {
      onChange(optionId);
      setOpen(false);
    }
  };

  const filteredOptions = normalizedOptions.filter((option) =>
    option.name?.toLowerCase().includes(inputValue?.toLowerCase())
  );

  return (
    <>
      {showSelectedOptions && selectedOptions.length > 0 && (
        <Grid container spacing={1} style={{ marginBottom: "20px" }}>
          {selectedOptions.map((selectedId) => {
            const option = normalizedOptions.find((option) => option.id === selectedId);
            return (
              option && (
                <Grid key={selectedId}>
                  <Chip
                    label={option.name}
                    onDelete={() => {
                      if (checkmark) {
                        onChange(selectedOptions.filter((id) => id !== selectedId));
                      } else {
                        onChange(""); 
                      }
                    }}
                    style={{ margin: "2px" }}
                  />
                </Grid>
              )
            );
          })}
        </Grid>
      )}

      <TextField
        label={label || undefined}
        style={{ width }}
        onClick={handleClick}
        value={
          Array.isArray(value)
            ? value
              .map((id) => normalizedOptions.find((option) => option.id === id)?.name)
              .join(", ")
            : normalizedOptions.find((option) => option.id === value)?.name || ""
        }
        placeholder={required ? `${label}*` : label}
        InputProps={{
          readOnly: true,
          endAdornment: selectedOptions.length > 0 && (
            <InputAdornment
              position="end"
              style={{ cursor: "pointer" }}
              onClick={() => onChange(checkmark ? [] : "")}
            >
              <Avatar style={{ width: 20, height: 20, fontSize: 12, padding: 1 }}>
                &#10006;
              </Avatar>
            </InputAdornment>
          ),
        }}
        variant={variant}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
      />

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} TransitionComponent={Fade}>
        <div>
          <TextField
            style={{ width, padding: 6 }}
            type="text"
            placeholder="Type here..."
            value={inputValue}
            onChange={handleInput}
            variant="standard"
          />
        </div>

        <div style={{ maxHeight: "250px", overflow: "auto" }}>
          {checkmark && (
            <MenuItem onClick={() => handleOptionChange("select_all")}>
              <Checkbox checked={selectedOptions.length === normalizedOptions.length} />
              Select All
            </MenuItem>
          )}

          {filteredOptions.length === 0 ? (
            <MenuItem disabled>No results found</MenuItem>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option.id}
                onClick={() => handleOptionChange(option.id)}
                style={{
                  background: selectedOptions.includes(option.id) ? "rgba(177, 209, 243, 0.664)" : "",
                }}
              >
                {checkmark && <Checkbox checked={selectedOptions.includes(option.id)} />}
                {option.name}
              </MenuItem>
            ))
          )}
        </div>
      </Menu>
    </>
  );
};

export default Dropdown;
